/**
 * FrontFaceGhostModels — 3D transparent ghost previews on the front face.
 *
 * Outer ghost (blue): represents the front face panel area.
 * Inner ghost (green): represents the display area.
 *
 * Uses body-shaped ExtrudeGeometry with bevel (like the real CSG panel)
 * so the ghost is visible at any inset value:
 *   - Small inset → ghost is a thin beveled slice at the front
 *   - Large inset → ghost is a tall beveled block (XY shrinks)
 */
import {useMemo, useEffect, useRef, useSyncExternalStore} from 'react'
import * as THREE from 'three'
import {createRoundedRectShape} from '../../../utils/geometry'
import {FRONT_FACE_GAP} from '../../workerGeo/helpers'
import {useAppSelector} from '../../../store'
import {selectActiveScene} from '../../store/scenesSlice'
import {getPreviewFrontFaceSnapshot, subscribePreviewFrontFace} from '../previewFrontFace'
import {GHOST_COLOR, GHOST_INNER_COLOR, GHOST_OPACITY} from './ghostStyle'

/** Slight size increase to stay above the real geometry */
const SIZE_OFFSET = 0.8

const FrontFaceGhostModels = () => {
  const preview = useSyncExternalStore(subscribePreviewFrontFace, getPreviewFrontFaceSnapshot)

  const reduxFrontFaceInset = useAppSelector((s) => selectActiveScene(s).frontFaceInset)
  const reduxDisplayCornerRadius = useAppSelector((s) => selectActiveScene(s).displayCornerRadius)
  const reduxDisplayInsetX = useAppSelector((s) => selectActiveScene(s).displayInsetX)
  const reduxDisplayInsetY = useAppSelector((s) => selectActiveScene(s).displayInsetY)

  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const deviceCornerRadius = useAppSelector((s) => selectActiveScene(s).deviceCornerRadius)
  const deviceEdgeBevel = useAppSelector((s) => selectActiveScene(s).deviceEdgeBevel)

  const frontFaceInset = preview?.frontFaceInset ?? reduxFrontFaceInset
  const displayCornerRadius = preview?.displayCornerRadius ?? reduxDisplayCornerRadius
  const displayInsetX = preview?.displayInsetX ?? reduxDisplayInsetX
  const displayInsetY = preview?.displayInsetY ?? reduxDisplayInsetY

  // Curved front face: inset controls Z depth, excess becomes XY shrinkage
  const clampedBevel = Math.min(deviceEdgeBevel, deviceWidth / 2, deviceHeight / 2, deviceDepth / 2)
  const innerW = Math.max(0.01, deviceWidth - 2 * clampedBevel)
  const innerH = Math.max(0.01, deviceHeight - 2 * clampedBevel)
  const innerD = Math.max(0.01, deviceDepth - 2 * clampedBevel)
  const frontZ = deviceDepth / 2
  const maxPlaneZ = frontZ - 0.01
  const effectivePlaneZ = Math.min(frontFaceInset, maxPlaneZ)
  const xyInset = Math.max(0, frontFaceInset - maxPlaneZ)
  const gap = FRONT_FACE_GAP

  // Outer ghost = front face panel area
  const panelW = Math.max(0.01, innerW - 2 * xyInset - 2 * gap)
  const panelH = Math.max(0.01, innerH - 2 * xyInset - 2 * gap)
  const panelCorner = Math.max(0, Math.min(deviceCornerRadius - xyInset - gap, panelW / 2, panelH / 2))

  // Inner ghost = display area
  const dw = Math.max(0.01, panelW - 2 * displayInsetX)
  const dh = Math.max(0.01, panelH - 2 * displayInsetY)
  const dcr = Math.min(displayCornerRadius, dw / 2, dh / 2)

  // ── Outer ghost geometry (body-shaped with bevel) ──────────────
  const prevOuterRef = useRef<THREE.BufferGeometry | null>(null)
  const outerGeo = useMemo(() => {
    // Build a body-shaped extrusion with bevel, like the real CSG panel
    const w = panelW + SIZE_OFFSET
    const h = panelH + SIZE_OFFSET
    const cr = Math.min(panelCorner, w / 2, h / 2)
    const shape = createRoundedRectShape(w, h, cr)
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: innerD,
      bevelEnabled: clampedBevel > 0,
      bevelThickness: clampedBevel,
      bevelSize: clampedBevel,
      bevelSegments: 3,
      curveSegments: 8,
    })
    // Center the full body shape at origin, then clip will be via position
    geo.translate(0, 0, -(innerD / 2))
    return geo
  }, [panelW, panelH, panelCorner, innerD, clampedBevel])

  useEffect(() => {
    if (prevOuterRef.current && prevOuterRef.current !== outerGeo) {
      prevOuterRef.current.dispose()
    }
    prevOuterRef.current = outerGeo
    return () => {
      outerGeo.dispose()
    }
  }, [outerGeo])

  // ── Inner ghost geometry (flat — display is a flat plane) ───────
  const prevInnerRef = useRef<THREE.ShapeGeometry | null>(null)
  const innerGeo = useMemo(() => {
    const cr = Math.min(dcr, dw / 2, dh / 2)
    const shape = createRoundedRectShape(dw, dh, cr)
    return new THREE.ShapeGeometry(shape, 8)
  }, [dw, dh, dcr])

  useEffect(() => {
    if (prevInnerRef.current && prevInnerRef.current !== innerGeo) {
      prevInnerRef.current.dispose()
    }
    prevInnerRef.current = innerGeo
    return () => {
      innerGeo.dispose()
    }
  }, [innerGeo])

  // Clipping plane — hides back portion, only shows from effectivePlaneZ forward
  const clippingPlane = useMemo(
    () => new THREE.Plane(new THREE.Vector3(0, 0, 1), -(effectivePlaneZ - SIZE_OFFSET / 2)),
    [effectivePlaneZ],
  )

  return (
    <group position={[0, 0, SIZE_OFFSET / 2]}>
      {/* Outer ghost — front face panel area (blue) */}
      <mesh geometry={outerGeo}>
        <meshBasicMaterial
          color={GHOST_COLOR}
          transparent
          opacity={GHOST_OPACITY}
          depthWrite={false}
          clippingPlanes={[clippingPlane]}
        />
      </mesh>
      {/* Inner ghost — display area (green), flat at frontZ */}
      <mesh geometry={innerGeo} position={[0, 0, frontZ]}>
        <meshBasicMaterial
          color={GHOST_INNER_COLOR}
          transparent
          opacity={GHOST_OPACITY}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  )
}

export default FrontFaceGhostModels
